import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Border style options
 */
const BORDER_STYLES = [
  { value: 'none', label: 'None' },
  { value: 'solid', label: 'Solid' },
  { value: 'dashed', label: 'Dashed' },
  { value: 'dotted', label: 'Dotted' },
  { value: 'double', label: 'Double' },
];

/**
 * BorderField - Compound field for border properties
 *
 * Example of how to create compound field types that combine multiple properties.
 * This pattern can be used for shadows, typography, and other grouped settings.
 *
 * Handles:
 * - Border width (px)
 * - Border style (solid, dashed, dotted, etc.)
 * - Border color
 * - Border radius (single value or per-corner)
 *
 * @param {Object} props
 * @param {Object} props.field - Field configuration
 * @param {string} props.field.key - Unique field identifier
 * @param {string} props.field.label - Display label
 * @param {Object} [props.field.default] - Default border object
 * @param {boolean} [props.field.showPerCornerRadius] - Show per-corner radius controls
 * @param {Object} props.value - Current border value
 * @param {Function} props.onChange - Callback when value changes
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {boolean} [props.collapsible] - Whether to show as collapsible (default: true)
 * @param {boolean} [props.defaultCollapsed] - Initial collapsed state (default: false)
 */
export default function BorderField({
  field,
  value,
  onChange,
  disabled = false,
  collapsible = true,
  defaultCollapsed = false,
}) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [showPerCorner, setShowPerCorner] = useState(false);

  // Normalize value to border object format
  const normalizeValue = (val) => {
    if (typeof val === 'object' && val !== null) {
      return {
        width: val.width ?? 0,
        style: val.style || 'solid',
        color: val.color || '#000000',
        radius: val.radius ?? 0,
        // Per-corner radius (optional)
        radiusTopLeft: val.radiusTopLeft ?? val.radius ?? 0,
        radiusTopRight: val.radiusTopRight ?? val.radius ?? 0,
        radiusBottomLeft: val.radiusBottomLeft ?? val.radius ?? 0,
        radiusBottomRight: val.radiusBottomRight ?? val.radius ?? 0,
      };
    }
    return field.default || {
      width: 0,
      style: 'solid',
      color: '#000000',
      radius: 0,
      radiusTopLeft: 0,
      radiusTopRight: 0,
      radiusBottomLeft: 0,
      radiusBottomRight: 0,
    };
  };

  const borderObj = normalizeValue(value);

  const updateBorder = (updates) => {
    // If updating single radius, also update all corners for consistency
    if ('radius' in updates && !showPerCorner) {
      updates.radiusTopLeft = updates.radius;
      updates.radiusTopRight = updates.radius;
      updates.radiusBottomLeft = updates.radius;
      updates.radiusBottomRight = updates.radius;
    }
    onChange({ ...borderObj, ...updates });
  };

  const renderContent = () => (
    <div className="space-y-3">
      {/* Border Width */}
      <div>
        <label className="block text-xs text-gray-600 mb-1">Width (px)</label>
        <input
          type="number"
          value={borderObj.width}
          onChange={(e) => updateBorder({ width: parseFloat(e.target.value) || 0 })}
          disabled={disabled}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
          min="0"
          max="20"
          step="1"
        />
      </div>

      {/* Border Style */}
      <div>
        <label className="block text-xs text-gray-600 mb-1">Style</label>
        <select
          value={borderObj.style}
          onChange={(e) => updateBorder({ style: e.target.value })}
          disabled={disabled}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
        >
          {BORDER_STYLES.map((style) => (
            <option key={style.value} value={style.value}>
              {style.label}
            </option>
          ))}
        </select>
      </div>

      {/* Border Color */}
      <div>
        <label className="block text-xs text-gray-600 mb-1">Color</label>
        <div className="flex items-center space-x-2">
          <input
            type="color"
            value={borderObj.color}
            onChange={(e) => updateBorder({ color: e.target.value })}
            disabled={disabled}
            className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
          />
          <input
            type="text"
            value={borderObj.color}
            onChange={(e) => updateBorder({ color: e.target.value })}
            disabled={disabled}
            className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
          />
        </div>
      </div>

      {/* Border Radius */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs text-gray-600">Radius (px)</label>
          {field.showPerCornerRadius !== false && (
            <button
              type="button"
              onClick={() => setShowPerCorner(!showPerCorner)}
              className="text-xs text-indigo-600 hover:text-indigo-800"
            >
              {showPerCorner ? 'Single value' : 'Per corner'}
            </button>
          )}
        </div>

        {!showPerCorner ? (
          <input
            type="number"
            value={borderObj.radius}
            onChange={(e) => updateBorder({ radius: parseFloat(e.target.value) || 0 })}
            disabled={disabled}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            min="0"
            max="100"
            step="1"
          />
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Top Left</label>
              <input
                type="number"
                value={borderObj.radiusTopLeft}
                onChange={(e) => updateBorder({ radiusTopLeft: parseFloat(e.target.value) || 0 })}
                disabled={disabled}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                min="0"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Top Right</label>
              <input
                type="number"
                value={borderObj.radiusTopRight}
                onChange={(e) => updateBorder({ radiusTopRight: parseFloat(e.target.value) || 0 })}
                disabled={disabled}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                min="0"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Bottom Left</label>
              <input
                type="number"
                value={borderObj.radiusBottomLeft}
                onChange={(e) => updateBorder({ radiusBottomLeft: parseFloat(e.target.value) || 0 })}
                disabled={disabled}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                min="0"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Bottom Right</label>
              <input
                type="number"
                value={borderObj.radiusBottomRight}
                onChange={(e) => updateBorder({ radiusBottomRight: parseFloat(e.target.value) || 0 })}
                disabled={disabled}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                min="0"
              />
            </div>
          </div>
        )}
      </div>

      {/* Preview */}
      {borderObj.width > 0 && (
        <div className="mt-2">
          <label className="block text-xs text-gray-600 mb-1">Preview</label>
          <div
            className="h-12 bg-white"
            style={{
              border: `${borderObj.width}px ${borderObj.style} ${borderObj.color}`,
              borderRadius: showPerCorner
                ? `${borderObj.radiusTopLeft}px ${borderObj.radiusTopRight}px ${borderObj.radiusBottomRight}px ${borderObj.radiusBottomLeft}px`
                : `${borderObj.radius}px`,
            }}
          />
        </div>
      )}
    </div>
  );

  if (!collapsible) {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-2">
          {field.label}
        </label>
        {renderContent()}
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded">
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <label className="text-xs font-medium text-gray-700 cursor-pointer">
          {field.label}
        </label>
        {isCollapsed ? (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronUp className="w-4 h-4 text-gray-500" />
        )}
      </button>
      {!isCollapsed && (
        <div className="p-3">
          {renderContent()}
        </div>
      )}
    </div>
  );
}
